import { useEffect, useState } from 'react'
import { Button, IconButton, InformationModal, Resource } from 'utoniq-ui'

import {
  ConsoleEventEditDetailFragment,
  EventStatus,
  Image,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  FixedButtonArea,
  FixedButtonAreaProps,
} from '@/components/pages/ConsoleEventCreate/template/components'
import { EventCreateFormProps } from '@/components/pages/ConsoleEventCreate/template/EventCreateForm'
import { ArtistThemeProvider } from '@/components/styles'
import { useForm, useMessage, useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { Place, Prediction } from '@/lib/googleMap'
import { encodeUrl } from '@/lib/url'

import {
  defaultValues,
  EditEventValue,
  EventEditForm,
  EventEditFormProps,
  schema,
} from './EventEditForm'

export * from './fragments'

export type State = {
  artistId: string
  event: Maybe<ConsoleEventEditDetailFragment>
  progress: EventEditFormProps['fileUploadInfo']['progress']
  predictions: Prediction[]
  place: Maybe<Place>
  gpsClientInfo: EventEditFormProps['gpsClientInfo']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: FixedButtonAreaProps['onSubmit']
  onDrop: EventCreateFormProps['fileUploadInfo']['onDrop']
  onPlaceSearch: (value: { keyword: string }) => Promise<void>
  onSelectPlace: (value: {
    placeId: string
    map: google.maps.Map
  }) => Promise<void>
  onRequestGPSPermission: () => void
}

export const ConsoleEventEditTemplate: Template<State, Action> = ({
  state,
  onDrop,
  onSubmit,
  onRequestGPSPermission,
  onSelectPlace,
  onPlaceSearch,
}) => {
  const { t } = useTranslation('consoleEventEdit')
  const { router, routerNames } = useRouter()
  const { showMessage } = useMessage()
  const [openCancel, setOpenCancel] = useState<boolean>(false)
  const method = useForm<EditEventValue>({ schema, defaultValues })

  useEffect(() => {
    if (!state.event) {
      return
    }
    method.reset({
      title: state.event.title ?? '',
      description: state.event.description ?? '',
      thumbnailPath: state.event.thumbnail?.mediumUri ?? '',
      thumbnailResource: buildResource(state.event.thumbnail),
      note: state.event.note ?? '',
      startedAt: state.event.startedAt ?? null,
      endedAt: state.event.endedAt ?? null,
      location: {
        latitude: state.event.location?.latitude ?? 0,
        longitude: state.event.location?.longitude ?? 0,
        approximateAddress: state.event.location?.approximateAddress ?? '',
        mapUri: encodeUrl(state.event.location?.mapUri) ?? '',
      },
      closeDate: state.event.closeDate?.length
        ? state.event.closeDate?.map(date => ({ date }))
        : [{ date: null }],
      isPublished: state.event.status === EventStatus.Published,
    })
  }, [state.event])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        margin
        header={{
          left: (
            <IconButton.Close
              edge="start"
              size="medium"
              color="white"
              onClick={() => {
                if (method.formState.isDirty) {
                  return setOpenCancel(true)
                }
                router.goBack()
              }}
            />
          ),
          title: state.event?.title,
          type: 'modal',
        }}
        customNavigation={
          <FixedButtonArea mode="update" method={method} onSubmit={onSubmit} />
        }
      >
        <Head title={routerNames.ConsoleShokufesEventEdit} />
        <EventEditForm
          artistId={state.artistId}
          method={method}
          fileUploadInfo={{ progress: state.progress, onDrop }}
          predictions={state.predictions}
          place={state.place}
          onSelectPlace={onSelectPlace}
          onPlaceSearch={onPlaceSearch}
          gpsClientInfo={state.gpsClientInfo}
          onRequestGPSPermission={onRequestGPSPermission}
        />
        <InformationModal
          title={t('updateCancelCreateEventModalTitle')}
          content={t('updateCancelCreateEventModalDesc')}
          open={openCancel}
          onClose={() => setOpenCancel(false)}
          cancelText={t('close')}
          buttons={
            <Button
              color="primary"
              size="large"
              onClick={() => {
                showMessage(t('updateCancelCreateEventSuccess'), 'success')
                router.goBack()
              }}
              sx={{ minWidth: 212 }}
            >
              {t('updateCancelCreateEventBtn')}
            </Button>
          }
          sx={{
            // 単に props で指定するだけだと
            // default のスタイルが優先されるため
            // セレクタを用いた CSS 指定を行っている
            '& .MuiBackdrop-root': {
              // 画面幅に依らずバックドロップを画面全体にかける
              maxWidth: '100%',
            },
          }}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

export function buildResource(
  resource?: Maybe<Partial<Image>>
): Resource | null {
  if (!resource) {
    return null
  }
  return {
    id: resource.path ?? '',
    resourceName: resource.path ?? '',
    imageUrl: resource.mediumUri ?? '',
    imageUploadPath: undefined, // 変更したときみpathを入れる
  }
}
